import { useRef, useState } from "react";
import { Upload, X } from "lucide-react";
import { api } from "../../lib/api";
import { todayStr } from "../crew/crewUtils";

const SERVICE_OPTIONS = [
  "Excavation",
  "Concrete & Foundations",
  "Site Prep & Grading",
  "Decorative & Stamped Concrete",
  "Retaining Walls",
  "Driveways, Paving & Surfacing",
  "Drainage",
];

const PHASES = [
  { value: "", label: "No phase" },
  { value: "before", label: "Before" },
  { value: "during", label: "During" },
  { value: "after", label: "After" },
];

const QUICK_TAGS = ["Material Arrived", "Weather Delay", "Inspection Ready", "Issue on Site", "Completed"];

const EMPTY_FORM = () => ({ job_site_name: "", location: "", date: todayStr(), service_type: "Excavation", phase: "", description: "", quick_tags: [] });

const inputCls = "w-full bg-white border-2 border-[#2C3539]/15 focus:border-[#2F5F80] focus:outline-none px-4 py-3 text-[#2C3539] font-medium";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs tracking-[0.2em] font-bold uppercase text-[#2C3539]/80 mb-2">{label}</span>
      {children}
    </label>
  );
}

export function UploadPanel({ onUploaded }) {
  const fileRef = useRef(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [files, setFiles] = useState([]); // [{file, preview, phase}]
  const [status, setStatus] = useState({ kind: "idle", msg: "" });

  const addFiles = (fileList) => {
    const all = Array.from(fileList || []);
    const incoming = all.slice(0, 12 - files.length);
    if (all.length > incoming.length) {
      setStatus({ kind: "error", msg: `Limit is 12 photos per batch — only the first ${incoming.length} were added.` });
    }
    if (!incoming.length) return;
    setFiles((prev) => [
      ...prev,
      ...incoming.map((f) => ({ file: f, preview: URL.createObjectURL(f), phase: form.phase || "" })),
    ]);
  };

  const removeFileAt = (idx) => {
    setFiles((prev) => {
      URL.revokeObjectURL(prev[idx]?.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const setFilePhase = (idx, phase) => {
    setFiles((prev) => prev.map((f, i) => (i === idx ? { ...f, phase } : f)));
  };

  const toggleTag = (t) => {
    setForm((f) => ({ ...f, quick_tags: f.quick_tags.includes(t) ? f.quick_tags.filter((x) => x !== t) : [...f.quick_tags, t] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!files.length) { setStatus({ kind: "error", msg: "Please choose at least one photo." }); return; }
    let done = 0;
    const failed = [];
    for (const item of files) {
      setStatus({ kind: "sending", msg: `Uploading photo ${done + 1} of ${files.length}…` });
      const fd = new FormData();
      fd.append("job_site_name", form.job_site_name);
      fd.append("location", form.location);
      fd.append("date", form.date);
      fd.append("service_type", form.service_type);
      fd.append("phase", item.phase || "");
      fd.append("description", form.description || "");
      fd.append("quick_tags", (form.quick_tags || []).join(","));
      fd.append("photo", item.file);
      try {
        await api.post("/jobs", fd, { headers: { "Content-Type": "multipart/form-data" } });
        done += 1;
      } catch (err) {
        failed.push(item.file.name);
      }
    }
    if (failed.length === 0) {
      setStatus({ kind: "ok", msg: `${done} photo${done === 1 ? "" : "s"} uploaded — visible on the public Past Jobs page.` });
      setForm(EMPTY_FORM());
      files.forEach((f) => URL.revokeObjectURL(f.preview));
      setFiles([]);
      if (fileRef.current) fileRef.current.value = "";
    } else {
      setStatus({ kind: "error", msg: `${done} uploaded, ${failed.length} failed (${failed.join(", ")}). Failed ones are still selected — try again.` });
      setFiles((prev) => prev.filter((f) => failed.includes(f.file.name)));
    }
    onUploaded?.();
  };

  return (
    <section className="bg-white border-2 border-[#2C3539]/10 p-6 md:p-10 mb-6" data-testid="upload-panel">
      <div className="font-display text-3xl md:text-4xl uppercase text-[#2C3539]">Upload a Job Photo</div>
      <p className="mt-2 text-sm text-[#2C3539]/70">Photos appear instantly on the public Past Jobs gallery. Only the job site name is required — the date defaults to today.</p>

      <form onSubmit={submit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="upload-form">
        <Field label="Job Site Name *">
          <input data-testid="upload-name" required value={form.job_site_name} onChange={(e) => setForm({...form, job_site_name: e.target.value})} className={inputCls} placeholder="e.g. Maple Bay Foundation" />
        </Field>
        <Field label="Location">
          <input data-testid="upload-location" value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} className={inputCls} placeholder="e.g. Duncan, BC"/>
        </Field>
        <Field label="Date (defaults to today)">
          <input data-testid="upload-date" type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className={inputCls}/>
        </Field>
        <Field label="Service Type">
          <select data-testid="upload-service" value={form.service_type} onChange={(e) => setForm({...form, service_type: e.target.value})} className={inputCls}>
            {SERVICE_OPTIONS.map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Photo Phase (default for newly added photos)">
          <select data-testid="upload-phase" value={form.phase} onChange={(e) => setForm({...form, phase: e.target.value})} className={inputCls}>
            {PHASES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </Field>
        <div className="md:col-span-2">
          <span className="block text-xs tracking-[0.2em] font-bold uppercase text-[#2C3539]/80 mb-2">Quick Progress Tags (tap all that apply)</span>
          <div className="flex flex-wrap gap-2" data-testid="upload-quick-tags">
            {QUICK_TAGS.map((t) => {
              const on = form.quick_tags.includes(t);
              return (
                <button
                  type="button"
                  key={t}
                  onClick={() => toggleTag(t)}
                  data-testid={`upload-tag-${t.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className={`min-h-[48px] px-4 text-xs font-bold uppercase tracking-[0.1em] border-2 transition-colors active:scale-95 ${on ? "bg-[#2F5F80] border-[#2F5F80] text-white" : "border-[#2C3539]/25 text-[#2C3539] hover:border-[#2F5F80] hover:text-[#2F5F80]"}`}
                >
                  {t}
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 text-xs text-[#2C3539]/55">Tags show up on the office Site Feed so everyone knows the state of the job instantly.</p>
        </div>
        <div className="md:col-span-2">
          <Field label="Notes (optional)">
            <textarea data-testid="upload-description" rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className={inputCls} placeholder="Anything notable about the job — scope, finish, conditions"/>
          </Field>
        </div>
        <div className="md:col-span-2">
          <label className="block">
            <span className="block text-xs tracking-[0.2em] font-bold uppercase text-[#2C3539]/80 mb-2">Photos * (up to 12 at once)</span>
            <div
              onClick={() => fileRef.current?.click()}
              data-testid="upload-dropzone"
              className="cursor-pointer border-2 border-dashed border-[#2C3539]/30 hover:border-[#2F5F80] bg-[#F4F5F6] hover:bg-[#2F5F80]/5 transition-colors px-6 py-8 text-center"
            >
              <div className="flex flex-col items-center gap-2 text-[#2C3539]">
                <Upload size={28} className="text-[#2F5F80]"/>
                <div className="font-display text-xl uppercase">{files.length ? "Click to add more photos" : "Click to select photos"}</div>
                <div className="text-xs text-[#2C3539]/60">JPEG, PNG, WEBP up to 15MB each — select several at once</div>
              </div>
            </div>
            <input ref={fileRef} data-testid="upload-photo" type="file" accept="image/*" multiple onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} className="hidden"/>
          </label>

          {files.length > 0 && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="upload-file-list">
              {files.map((f, idx) => (
                <div key={f.preview} className="border-2 border-[#2C3539]/15 bg-white" data-testid={`upload-file-${idx}`}>
                  <div className="relative aspect-[4/3] bg-[#2C3539]">
                    <img src={f.preview} alt={f.file.name} className="w-full h-full object-cover"/>
                    <button type="button" onClick={() => removeFileAt(idx)} data-testid={`upload-file-remove-${idx}`} aria-label="Remove photo" className="absolute top-1.5 right-1.5 bg-[#23282B]/80 hover:bg-red-600 text-white p-1">
                      <X size={12}/>
                    </button>
                  </div>
                  <div className="p-2">
                    <div className="text-[10px] text-[#2C3539]/60 truncate">{f.file.name}</div>
                    <select
                      data-testid={`upload-file-phase-${idx}`}
                      value={f.phase}
                      onChange={(e) => setFilePhase(idx, e.target.value)}
                      className="mt-1 w-full text-xs border border-[#2C3539]/25 px-2 py-1.5 bg-white text-[#2C3539] focus:border-[#2F5F80] focus:outline-none"
                    >
                      {PHASES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {status.kind === "error" && <div className="md:col-span-2 bg-red-50 border-l-4 border-red-500 px-4 py-3 text-sm text-red-700" data-testid="upload-error">{status.msg}</div>}
        {status.kind === "ok" && <div className="md:col-span-2 bg-[#2F5F80]/10 border-l-4 border-[#2F5F80] px-4 py-3 text-sm text-[#2C3539]" data-testid="upload-success">{status.msg}</div>}

        <div className="md:col-span-2">
          <button type="submit" disabled={status.kind === "sending"} data-testid="upload-submit"
                  className="inline-flex items-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white font-display tracking-wider px-8 py-4 uppercase disabled:opacity-50">
            <Upload size={18}/> {status.kind === "sending" ? status.msg : `Publish ${files.length > 1 ? `${files.length} Photos` : "Job"}`}
          </button>
        </div>
      </form>
    </section>
  );
}
